import { Close, FlashOff, FlashOn } from '@mui/icons-material';
import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { BarcodeFormat, BarcodeScanner, DetectedBarcode, useTorch } from 'react-barcode-scanner';
// Installe le décodeur de rechange (WASM) lorsque l'API native BarcodeDetector
// n'est pas disponible dans le navigateur (Safari/iOS, Firefox, etc.).
import 'react-barcode-scanner/polyfill';

export interface BarcodeScanDialogProps {
    open: boolean;
    onClose: () => void;
    onDetected: (code: string) => void;
}

// Formats de code à barres pertinents pour les produits (UPC/EAN) et les
// étiquettes internes alphanumériques (Code 128/39). La liste peut être
// ajustée si d'autres formats sont utilisés sur le terrain.
const SCANNED_BARCODE_FORMATS = [
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39
];

export const BarcodeScanDialog = (props: BarcodeScanDialogProps) => {
    const { open, onClose, onDetected } = props;
    const hasCapturedRef = React.useRef(false);
    const [cameraError, setCameraError] = React.useState<string | null>(null);
    const { isTorchSupported, isTorchOn, setIsTorchOn } = useTorch();

    React.useEffect(() => {
        if (open) {
            hasCapturedRef.current = false;
            setCameraError(null);
        } else {
            setIsTorchOn(false);
        }
    }, [open, setIsTorchOn]);

    const onCapture = (barcodes: DetectedBarcode[]) => {
        if (hasCapturedRef.current) return;

        const value = barcodes[0]?.rawValue;
        if (!value) return;

        hasCapturedRef.current = true;
        onDetected(value);
        onClose();
    };

    const onCameraErrorHandler = (error: Error) => {
        console.error('Erreur d’accès à la caméra pour la lecture de code à barre', error);
        setCameraError("Impossible d'accéder à la caméra. Vérifiez que l'accès à la caméra a été autorisé pour ce site.");
    };

    return <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        slotProps={{ paper: { sx: { bgcolor: 'black' } } }}
    >
        <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {open && !cameraError && <BarcodeScanner
                options={{ formats: SCANNED_BARCODE_FORMATS }}
                onCapture={onCapture}
                onCameraError={onCameraErrorHandler}
            />}
            <Box sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                pointerEvents: 'none'
            }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, pointerEvents: 'auto' }}>
                    <IconButton onClick={onClose} aria-label="Fermer" sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}>
                        <Close />
                    </IconButton>
                    {isTorchSupported && <IconButton
                        onClick={() => setIsTorchOn(!isTorchOn)}
                        aria-label="Lampe de poche"
                        sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)' }}
                    >
                        {isTorchOn ? <FlashOn /> : <FlashOff />}
                    </IconButton>}
                </Stack>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {!cameraError && <Box sx={{
                        width: '75%',
                        maxWidth: 360,
                        aspectRatio: '16 / 9',
                        border: '3px solid rgba(255,255,255,0.85)',
                        borderRadius: 2
                    }} />}
                </Box>
                <Stack alignItems="center" sx={{ p: 3 }}>
                    <Typography align="center" sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.4)', px: 2, py: 1, borderRadius: 1 }}>
                        {cameraError ?? 'Placez le code à barre dans le cadre'}
                    </Typography>
                </Stack>
            </Box>
        </Box>
    </Dialog>;
};
